import type { ChoiceEffect, FactionId, StatKey } from '@/types';
import {
  useCharacterStore,
  useWorldStore,
  useNarrativeStore,
} from '@/engine/state';

/**
 * Aplica un efecto inmediato al estado del juego mutando los stores.
 * Devuelve una descripción corta en español (para el log de decisiones).
 * Retorna null si el efecto no produce una entrada visible en el log.
 */
export function aplicarEfecto(e: ChoiceEffect): string | null {
  const character = useCharacterStore.getState();
  const world = useWorldStore.getState();
  const narrative = useNarrativeStore.getState();

  switch (e.kind) {
    case 'stat': {
      character.modificarStat(e.stat, e.delta);
      return descripcionStat(e.stat, e.delta);
    }
    case 'xp': {
      character.ganarXp(e.cantidad);
      return `+${e.cantidad} XP`;
    }
    case 'item': {
      ajustarInventario(e.itemId, e.delta);
      return e.delta > 0 ? `Obtuviste «${e.itemId}»` : `Perdiste «${e.itemId}»`;
    }
    case 'flag': {
      world.setFlag(e.flagId, e.valor);
      return null;
    }
    case 'reputation': {
      world.modificarReputacion(e.facciónId, e.delta);
      return descripcionReputacion(e.facciónId, e.delta);
    }
    case 'unlockSkill': {
      desbloquearSkill(e.skillId);
      return `Habilidad desbloqueada: ${e.skillId}`;
    }
    case 'unlockSpell': {
      aprenderHechizo(e.hechizoId);
      return `Aprendiste: ${e.hechizoId}`;
    }
    case 'unlockLocation': {
      world.desbloquearLocacion(e.locacionId);
      return `Nueva locación: ${e.locacionId}`;
    }
    case 'scheduleConsequence': {
      // El engine se encarga de asociar la decisión actual al consequence.
      // Acá solo marcamos el scheduling; el vinculado lo hace sceneEngine.
      void narrative;
      return null;
    }
  }
}

function descripcionStat(stat: StatKey, delta: number): string {
  const signo = delta > 0 ? '+' : '';
  const nombres: Record<StatKey, string> = {
    vida: 'Vida',
    vidaMax: 'Vida máx.',
    mana: 'Mana',
    manaMax: 'Mana máx.',
    empatia: 'Empatía',
    valentia: 'Valentía',
    astucia: 'Astucia',
    resistencia: 'Resistencia',
    vinculo: 'Vínculo con Pipo',
  };
  return `${signo}${delta} ${nombres[stat]}`;
}

function descripcionReputacion(f: FactionId, delta: number): string {
  const nombres: Record<FactionId, string> = {
    clan_rio: 'Clan del Río',
    guardianes_fuego: 'Guardianes del Fuego',
    susurradores: 'Susurradores',
    sombra: 'La Sombra',
  };
  const signo = delta > 0 ? '+' : '';
  return `${signo}${delta} reputación con ${nombres[f]}`;
}

function ajustarInventario(itemId: string, delta: number): void {
  useCharacterStore.setState((s) => {
    const existente = s.personaje.inventario.find((i) => i.id === itemId);
    let nuevoInv = s.personaje.inventario;
    if (existente) {
      const nuevaCant = existente.cantidad + delta;
      nuevoInv =
        nuevaCant <= 0
          ? s.personaje.inventario.filter((i) => i.id !== itemId)
          : s.personaje.inventario.map((i) =>
              i.id === itemId ? { ...i, cantidad: nuevaCant } : i,
            );
    } else if (delta > 0) {
      nuevoInv = [
        ...s.personaje.inventario,
        {
          id: itemId,
          nombre: itemId,
          descripcion: '',
          tipo: 'narrativo',
          cantidad: delta,
        },
      ];
    }
    return { personaje: { ...s.personaje, inventario: nuevoInv } };
  });
  useCharacterStore.getState().persistir();
}

function desbloquearSkill(skillId: string): void {
  useCharacterStore.setState((s) => {
    if (s.personaje.skillsDesbloqueadas.includes(skillId)) return s;
    return {
      personaje: {
        ...s.personaje,
        skillsDesbloqueadas: [...s.personaje.skillsDesbloqueadas, skillId],
      },
    };
  });
  useCharacterStore.getState().persistir();
}

function aprenderHechizo(hechizoId: string): void {
  useCharacterStore.setState((s) => {
    if (s.personaje.hechizosAprendidos.includes(hechizoId)) return s;
    return {
      personaje: {
        ...s.personaje,
        hechizosAprendidos: [...s.personaje.hechizosAprendidos, hechizoId],
      },
    };
  });
  useCharacterStore.getState().persistir();
}

/** Aplica una lista de efectos en orden, devuelve los resúmenes no nulos. */
export function aplicarEfectos(efectos: ChoiceEffect[] | undefined): string[] {
  if (!efectos) return [];
  const resumenes: string[] = [];
  for (const e of efectos) {
    const r = aplicarEfecto(e);
    if (r) resumenes.push(r);
  }
  return resumenes;
}
